import { Controller } from 'react-hook-form';
import { Activity } from 'react';
import {
  InputText,
  DateInput,
  formatBalance,
  Checkbox,
  IconSettings,
  ButtonSize,
  ButtonPseudo,
  ToggleInput,
  formatDate,
  IconLoader,
} from '@ui/src';
import FormRow from './form-row';
import AccountBalance from '../balance/balance';
import { AccountFormTypes } from './types';

import cn from './styles.module.css';
import sharedStyles from '../shared.module.css';

const AccountForm = ({
  control,
  errors,
  accountId,
  balances = [],
  currencies,
  baseCurrencyId,
  totalBalanceInBC,
  clientAccountError,
  creditBlock,
  debitBlock,
  setCreditBlock,
  setDebitBlock,
  setCreditBlockConfirmationVisible,
  setDebitBlockConfirmationVisible,
  setAvailableCurrenciesVisible,
  currenciesList,
  overdraftEnabled,
  onUpdateOverdraftStatus,
  overdraftUpdateInProgress,
  overdraftEnabledSetting,
  setBalances,
  updateOverdraftBalanceIsLoading,
  defaultBalances,
  closingBalancesAt,
  closedBalances,
  setClosingBalancesAt,
  closingBalanceAtSearchParam,
  closingBalancesLoading,
}: AccountFormTypes) => {
  const balancesWithoutBaseCurrency = balances
    .filter((item) => item.currencyId !== baseCurrencyId)
    .map((item) => ({
      ...item,
      currencyName: currencies?.find(
        (currency) => currency.value === item.currencyId,
      )?.suffix,
    }))
    .sort((a, b) =>
      (a?.currencyName as string)?.localeCompare(b?.currencyName as string),
    );

  const baseCurrencyBalance = balances.find(
    (item) => item.currencyId === baseCurrencyId,
  );

  return (
    <>
      <div className={cn.balanceWrapper}>
        <div className={cn.balanceTitle}>Account blocking</div>
        <div className={cn.blockAccountWrapper}>
          <Checkbox
            type="checkbox"
            name="creditBlock"
            checked={creditBlock}
            onChange={(e) => {
              setCreditBlock(e.target.checked);

              if (accountId) {
                setCreditBlockConfirmationVisible(true);
              }
            }}
            title="Block top-up"
          />
        </div>
        <div className={cn.blockAccountWrapper}>
          <Checkbox
            type="checkbox"
            name="debitBlock"
            checked={debitBlock}
            onChange={(e) => {
              setDebitBlock(e.target.checked);

              if (accountId) {
                setDebitBlockConfirmationVisible(true);
              }
            }}
            title="Block withdraw"
          />
        </div>
      </div>
      {accountId && (
        <div className={cn.balanceWrapper}>
          <div className={cn.balanceTitle}>
            <div className={cn.balanceTitleLeft}>
              Balance
              <ButtonPseudo
                text="Available currencies"
                icon={<IconSettings />}
                size={ButtonSize.SMALL}
                className={`button-white ${cn.availableCurrenciesButton}`}
                onClick={() => setAvailableCurrenciesVisible(true)}
              />
            </div>
            {overdraftEnabledSetting && (
              <ToggleInput
                name="overdraftEnabled"
                title="Overdraft"
                checked={overdraftEnabled}
                disabled={overdraftUpdateInProgress}
                onChange={(e) => onUpdateOverdraftStatus(e.target.checked)}
              />
            )}
          </div>
          {!closingBalanceAtSearchParam && (
            <div className={cn.closingBalancesDateWrapper}>
              <DateInput
                placeholder="Select balance date"
                value={closingBalancesAt}
                onChange={(e) => {
                  setClosingBalancesAt(new Date(e).toISOString());
                }}
                onReset={() => {
                  setClosingBalancesAt('');
                }}
                withoutCustomSelector
              />
            </div>
          )}
          {(baseCurrencyBalance || balances?.length > 0) && (
            <div
              className={`${cn.balancesHeader} ${
                closingBalancesAt ? cn.flexStart : ''
              }`}
            >
              <div className={cn.balancesHeaderLeft}>Balance</div>
              {closingBalancesAt && (
                <div
                  className={`${cn.balancesHeaderLeft} ${cn.closedBalancesHeader}`}
                >{`Balance on ${formatDate(closingBalancesAt)}`}</div>
              )}
              {overdraftEnabledSetting && overdraftEnabled && (
                <div className={cn.balancesHeaderRight}>Overdraft limit</div>
              )}
            </div>
          )}

          <div className={cn.balancesWrapper}>
            {closingBalancesLoading && (
              <div className={cn.closingBalanceLoader}>
                <IconLoader />
              </div>
            )}
            {baseCurrencyBalance && (
              <AccountBalance
                balance={{
                  ...baseCurrencyBalance,
                  currencyName: currencies?.find(
                    (currency) =>
                      currency.value === baseCurrencyBalance.currencyId,
                  )?.suffix,
                }}
                closedBalance={
                  closedBalances
                    ?.find(
                      (item) =>
                        item.currencyId === baseCurrencyBalance.currencyId,
                    )
                    ?.value?.toString()
                    ? formatBalance(
                        closedBalances?.find(
                          (item) =>
                            item.currencyId === baseCurrencyBalance.currencyId,
                        )?.value as number,
                        closedBalances?.find(
                          (item) =>
                            item.currencyId === baseCurrencyBalance.currencyId,
                        )?.currency,
                      )
                    : undefined
                }
                currenciesList={currenciesList}
                overdraftEnabled={overdraftEnabledSetting && overdraftEnabled}
                balances={balances}
                setBalances={setBalances}
                updateOverdraftBalanceIsLoading={
                  updateOverdraftBalanceIsLoading
                }
                defaultBalance={defaultBalances?.find(
                  (item) => item.id === baseCurrencyBalance.id,
                )}
              />
            )}
            {balances.length > 0 &&
              balancesWithoutBaseCurrency.map((item) => (
                <AccountBalance
                  key={item.id}
                  balance={item}
                  currenciesList={currenciesList}
                  overdraftEnabled={overdraftEnabledSetting && overdraftEnabled}
                  balances={balances}
                  setBalances={setBalances}
                  updateOverdraftBalanceIsLoading={
                    updateOverdraftBalanceIsLoading
                  }
                  closedBalance={
                    closedBalances
                      ?.find(
                        (balance) => balance.currencyId === item.currencyId,
                      )
                      ?.value?.toString()
                      ? formatBalance(
                          closedBalances?.find(
                            (balance) => balance.currencyId === item.currencyId,
                          )?.value as number,
                          closedBalances?.find(
                            (balance) => balance.currencyId === item.currencyId,
                          )?.currency,
                        )
                      : undefined
                  }
                  defaultBalance={defaultBalances?.find(
                    (balance) => balance.id === item.id,
                  )}
                />
              ))}
            {balances.length > 0 && (
              <div
                className={sharedStyles.balanceItem}
                style={{ display: 'none' }}
              >
                <div className={sharedStyles.balanceItemLeft}>
                  <span>Total:</span>
                  <span>
                    {typeof totalBalanceInBC === 'number'
                      ? formatBalance(
                          totalBalanceInBC,
                          currenciesList?.find(
                            (currency) => currency.id === baseCurrencyId,
                          ),
                        )
                      : '-'}
                  </span>
                </div>
              </div>
            )}
            {!balances.length && (
              <div className={cn.emptyBalances}>No available balances yet</div>
            )}
          </div>
        </div>
      )}
      <FormRow title="Parent category">
        <Controller
          control={control}
          defaultValue=""
          name="categoryName"
          render={({ field: { value } }) => (
            <InputText
              name="categoryName"
              value={value}
              placeholder=""
              disabled
            />
          )}
        />
      </FormRow>
      <FormRow title="Parent group">
        <Controller
          control={control}
          defaultValue=""
          name="groupName"
          render={({ field: { value } }) => (
            <InputText name="groupName" value={value} placeholder="" disabled />
          )}
        />
      </FormRow>
      <FormRow title="Parent primary account">
        <Controller
          control={control}
          defaultValue=""
          name="primaryAccountName"
          render={({ field: { value } }) => (
            <InputText
              name="primaryAccountName"
              value={value}
              placeholder=""
              disabled
            />
          )}
        />
      </FormRow>
      <FormRow title="Description">
        <Controller
          control={control}
          defaultValue=""
          name="description"
          render={({ field: { value, onChange } }) => (
            <InputText
              name="description"
              value={value}
              placeholder="Enter description..."
              onChange={onChange}
              error={errors?.description?.message}
            />
          )}
        />
      </FormRow>
      <FormRow title="Opened">
        <Controller
          control={control}
          defaultValue=""
          name="createdAt"
          render={({ field: { value, onChange } }) => (
            <DateInput
              className={accountId ? cn.pickerDisabled : ''}
              value={value}
              onChange={(date) => onChange(date.toISOString())}
              error={errors?.createdAt?.message}
              fullWidth
            />
          )}
        />
      </FormRow>
      <FormRow title="Account number">
        <Controller
          control={control}
          defaultValue=""
          name="fullNumber"
          render={({ field: { value } }) => (
            <InputText
              name="fullNumber"
              value={value}
              placeholder=""
              disabled
            />
          )}
        />
      </FormRow>
      <FormRow title="Type">
        <Controller
          control={control}
          defaultValue=""
          name="type"
          render={({ field: { value } }) => (
            <InputText name="type" value={value} placeholder="" disabled />
          )}
        />
      </FormRow>
      <Activity mode={accountId ? 'visible' : 'hidden'}>
        <Controller
          control={control}
          defaultValue={false}
          name="revaluable"
          render={({ field: { value, onChange } }) => (
            <Checkbox
              type="checkbox"
              name="revaluable"
              checked={value}
              onChange={(e) => onChange(e.target.checked)}
              title="Revaluable account"
              disabled
            />
          )}
        />
      </Activity>

      <Controller
        control={control}
        defaultValue=""
        name="primaryId"
        render={() => <input type="hidden" name="primaryId" />}
      />
      {clientAccountError && (
        <div className={cn.clientError}>{clientAccountError}</div>
      )}
    </>
  );
};

export default AccountForm;
